import React, { createContext, useContext, useState, useEffect } from "react";

// Define language types
export type Language = "es" | "en";

// Define language context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getBilingualText: (key: string) => string | JSX.Element;
}

// Create language context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Translations object
const translations = {
  es: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.miInversion": "Mi inversión",
    "nav.portfolio": "Portfolio",
    "nav.fees": "Fees",

    // Fees labels
    "fees.pageSize.label": "Tamaño de página",
    "fees.filter.status": "Estado",
    "fees.filter.all": "Todos",
    "fees.filter.paid": "Pagados",
    "fees.filter.pending": "Pendientes",
    "pagination.prev": "Anterior",
    "pagination.next": "Siguiente",
    "pagination.page": "Página",
    "pagination.of": "de",
    "nav.deals": "Pro-Ratas",
    "nav.reportes": "Reportes",
    "nav.reportes.financiero": "Información Financiera",
    "nav.reportes.operativo": "Reportes Operativos",
    "nav.reportes.documentacion": "Documentación",
    "nav.profile": "Perfil",
    "nav.academy": "The HNWI Academy",
    "nav.feedback": "Feedback",
    "nav.logout": "Cerrar sesión",

    // User info
    "user.role": "Socio",
    "user.profile": "Mi perfil",

    // Login
    "login.title": "Portal de Socios",
    "login.email": "Email",
    "login.password": "Contraseña",
    "login.button": "Acceder como Socio",
    "login.tagline": "Accede a tu cuenta para gestionar tus inversiones",
    "login.rememberMe": "Recordar usuario",
    "login.forgotPassword": "¿Olvidaste tu contraseña?",
    "login.or": "O",
    "login.support": "Contact Support: support@bellamysignature.com",

    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.welcome": "Bienvenido de nuevo,",
    "dashboard.overview": "Resumen de tu cartera",
    "dashboard.performance": "Rendimiento",
    "dashboard.nav.evolution": "Evolución NAV",
    "dashboard.nav.current": "NAV Actual",
    "dashboard.nav.ytd": "YTD",
    "dashboard.nav.live": "LIVE",
    "dashboard.nav.lastUpdated": "Last Updated",
    "dashboard.nav.totalValue": "Valor Total de la Cartera",
    "dashboard.nav.performanceTitle": "Rendimiento Global",
    "dashboard.nav.distributionTitle": "Distribución por Tipo",
    "dashboard.nav.evolutionTitle": "Evolución NAV",
    "dashboard.summary.totalInvested": "Total Invertido",
    "dashboard.summary.currentValue": "Valor Actual",
    "dashboard.summary.totalReturn": "Rentabilidad Total",
    "dashboard.summary.unrealizedGains": "Plusvalías No Realizadas",
    "dashboard.kpi.activoTotal": "Activo Total",
    "dashboard.kpi.rentasYTD": "Rentas YTD",
    "dashboard.kpi.cashOnCash": "Cash on Cash",
    "dashboard.kpi.feesYTD": "Fees YTD",
    "dashboard.kpi.feesTotal": "Total Fees",
    "profile.security.adminPasswordNote": "La contraseña solo puede ser cambiada por el administrador.",
    "dashboard.activity.title": "Actividad Reciente",
    "dashboard.activity.subtitle": "Últimos movimientos de tu cartera",
    "dashboard.activity.rent": "Renta",
    "dashboard.table.date": "Fecha",
    "dashboard.table.asset": "Activo",
    "dashboard.table.type": "Tipo",
    "dashboard.table.amount": "Importe",
    "dashboard.table.status": "Estado",
    "dashboard.status.completed": "Completado",

    // Portfolio
    "portfolio.title": "Portfolio",
    "portfolio.overview": "Resumen de inversiones",
    "portfolio.totalValue": "Valor Total",
    "portfolio.performance": "Rendimiento",
    "portfolio.allocation": "Asignación",
    "portfolio.tableHeaders.property": "Inmueble",
    "portfolio.tableHeaders.location": "Ubicación",
    "portfolio.tableHeaders.type": "Tipo",
    "portfolio.tableHeaders.area": "Superficie",
    "portfolio.tableHeaders.investment": "Inversión",
    "portfolio.tableHeaders.currentValue": "Valor Actual",
    "portfolio.tableHeaders.yield": "Rentabilidad",
    "portfolio.tableHeaders.status": "Estado",
    "portfolio.filters.all": "Todos",
    "portfolio.filters.residential": "Residencial",
    "portfolio.filters.commercial": "Comercial",
    "portfolio.filters.industrial": "Industrial",
    "portfolio.status.active": "Activo",
    "portfolio.status.sold": "Vendido",
    "portfolio.status.construction": "En construcción",
    "portfolio.header.title": "Portfolio",
    "portfolio.header.subtitle":
      "Visión completa de tus inversiones inmobiliarias",
    "portfolio.table.property": "Propiedad",
    "portfolio.table.location": "Ubicación",
    "portfolio.table.type": "Tipo",
    "portfolio.table.investment": "Inversión",
    "portfolio.table.currentValue": "Valor Actual",
    "portfolio.table.return": "Rentabilidad",
    "portfolio.table.status": "Estado",
    "portfolio.table.annualRent": "Rentas Anual",
    "portfolio.table.mortgage": "Hipoteca",
    "portfolio.table.purchaseDate": "Fecha Compra",
    "portfolio.table.firstRentDate": "Fecha 1º Alquiler",
    "portfolio.table.vacancy12m": "Vacancy 12M",
    "portfolio.filter.all": "Todas",
    "portfolio.filter.commercial": "Comercial",
    "portfolio.filter.residential": "Residencial",
    "portfolio.filter.industrial": "Industrial",
    "portfolio.status.development": "En Desarrollo",
    "portfolio.status.renting": "Alquilándose",
    "portfolio.status.repositioning": "Reposicionándose",
    "portfolio.status.underContract": "Bajo Arras",
    "portfolio.actions.viewDetail": "Ver detalle",
    "portfolio.totals.total": "TOTAL",
    "portfolio.totals.assets": "Activos",
    "portfolio.empty.noResults": "Oh, no parece que no hay nada por aquí",

    // Investment (Aportaciones) - Complete translations
    "investment.title": "Mi Inversión",
    "investment.summary": "Resumen de aportaciones",
    "investment.total": "Total invertido",
    "investment.date": "Fecha",
    "investment.amount": "Cantidad",
    "investment.status": "Estado",
    "investment.contributions": "Aportaciones",
    "investment.totalInvested": "Total Invertido",
    "investment.contributionsMade": "Aportaciones realizadas",
    "investment.averageContribution": "Aportación promedio",
    "investment.totalContributions": "Total de aportaciones",
    "investment.reference": "Referencia",
    "investment.method": "Método",
    "investment.pending": "Pendiente",
    "investment.completed": "Completado",
    "investment.processing": "Procesando",
    "investment.summary.totalContributed": "Total Aportado",
    "investment.summary.totalGains": "Ganancias Totales",
    "investment.header.title": "Evolución de tu Inversión",
    "investment.header.subtitle":
      "Comparativa entre capital aportado y valor actual de tu inversión",
    "investment.evolution.title": "Evolución de tu Inversión",
    "investment.evolution.subtitle":
      "Comparativa entre capital aportado y valor actual de tu inversión",
    "investment.metrics.completedContributions": "aportaciones completadas",
    "investment.metrics.performanceOverContributed": "sobre aportado",
    "investment.history.title": "Mi Inversión",
    "investment.history.subtitle":
      "Historial completo de tus aportaciones y rendimientos",
    "investment.table.concept": "Concepto",
    "investment.table.amount": "Importe",
    "investment.table.percentage": "Porcentaje",
    "investment.table.date": "Fecha",
    "investment.table.viewDocument": "Ver documento",
    "investment.table.noDocument": "Sin documento",
    "investment.table.status": "Estado",
    "investment.concepts.initial": "Aportación inicial",
    "investment.concepts.additional": "Aportación adicional",
    "investment.concepts.q3": "Aportación Q3",
    "investment.status.completed": "Completada",
    "investment.status.pending": "Pendiente",
    "investment.status.pendingRequest": "Solicitud pendiente",
    "investment.actions.contributeMore": "Aportar Más",
    "investment.actions.requestAuthorization": "Solicitar Autorización",
    "investment.actions.cancel": "Cancelar",
    "investment.actions.close": "Cerrar",
    "investment.tooltip.contributionsBlocked":
      "Aportaciones bloqueadas temporalmente. Solicita autorización para realizar una nueva aportación.",
    "investment.modal.request.title": "Solicitar nueva aportación",
    "investment.modal.request.description":
      "Actualmente el sistema de aportaciones está bloqueado. Puedes solicitar autorización para realizar una nueva aportación y nuestro equipo se pondrá en contacto contigo.",
    "investment.modal.request.amountLabel": "Cantidad deseada",
    "investment.modal.request.amountPlaceholder": "Ej. €50.000",
    "investment.modal.request.conceptLabel": "Concepto",
    "investment.modal.request.conceptPlaceholder": "Aportación adicional Q4",
    "investment.modal.success.title": "Solicitud enviada correctamente",
    "investment.modal.success.description":
      "Tu solicitud ha sido enviada correctamente. Nuestro equipo revisará la petición y se pondrá en contacto contigo en un plazo de 24-48h.",
    "investment.chart.totalContributed": "Total Aportado",
    "investment.chart.currentValue": "Valor Actual",

    // Deals
    "deals.title": "Pro-Ratas",
    "deals.subtitle":
      "Explora las últimas oportunidades de inversión inmobiliaria disponibles.",
    "deals.header.title": "Oportunidades Disponibles",
    "deals.active": "Oportunidades activas",
    "deals.new": "Nueva oportunidad",
    "deals.status": "Estado",
    "deals.type": "Tipo",
    "deals.location": "Ubicación",
    "deals.investment": "Inversión mínima",
    "deals.metrics.units": "UNIDADES",
    "deals.metrics.builtArea": "Metros construidos",
    "deals.metrics.roomPotential": "Potencial Habitaciones",
    "deals.commercial.locals": "Locales comerciales",
    "deals.sections.general": "General",
    "deals.sections.strategy": "Estrategia",
    "deals.sections.timing": "Timing",
    "deals.fields.name": "Nombre",
    "deals.fields.address": "Dirección",
    "deals.fields.city": "Localidad",
    "deals.fields.catastralRef": "Referencia Catastral",
    "deals.fields.strategy": "Estrategia",
    "deals.fields.typology": "Tipología",
    "deals.strategy.description":
      "Comprar, dividir, liquidar locales comerciales y explotar habitaciones",
    "deals.typology.residential": "Residencial y Comercial",
    "deals.timeline.negotiations": "Inicio negociaciones",
    "deals.timeline.contract": "Firma contrato de arras",
    "deals.timeline.purchase": "Fecha de compra fijada",
    "deals.actions.viewDetails": "Ver detalles",
    "deals.actions.investNow": "Invertir ahora",
    "deals.actions.viewMaps": "Click para abrir Google Maps",

    // Reports
    "reports.title": "Reportes",
    "reports.financial": "Información Financiera",
    "reports.operational": "Reportes Operativos",
    "reports.documentation": "Documentación",
    "reports.download": "Descargar",
    "reports.view": "Ver documento",
    "reports.monthly": "Reporte Mensual",
    "reports.quarterly": "Reporte Trimestral",
    "reports.annual": "Reporte Anual",
    "reports.status.available": "Disponible",
    "reports.status.processing": "Procesando",
    "reports.date": "Fecha",
    "reports.type": "Tipo",
    "reports.actions": "Acciones",
    "reports.financial.title": "Informes Financieros",
    "reports.financial.subtitle":
      "Estados financieros y reportes de rendimiento",
    "reports.operational.title": "Reportes Operativos",
    "reports.operational.subtitle":
      "Informes operativos y de gestión de propiedades",
    "reports.documentation.title": "Documentación Legal",
    "reports.documentation.subtitle":
      "Contratos, escrituras y documentos legales",
    "reports.tabs.financial": "Información Financiera",
    "reports.tabs.operational": "Reportes Operativos",
    "reports.tabs.documentation": "Documentación",

    // Document categories
    "docs.governance.title": "Governanza - Pacto de Socios",
    "docs.governance.subtitle":
      "Acuerdos y políticas de governanza corporativa",
    "docs.capitalExtensions.title": "Ampliaciones de Capital 2024",
    "docs.capitalExtensions.subtitle":
      "Documentación de operaciones de ampliación de capital",
    "docs.taxes.title": "Impuestos",
    "docs.taxes.subtitle": "Declaraciones fiscales y documentación tributaria",
    "docs.other.title": "Otra Documentación",
    "docs.other.subtitle": "Documentos adicionales y archivo corporativo",
    "docs.secure.title": "Documentación Segura",
    "docs.secure.subtitle":
      "Todos los documentos están almacenados de forma segura en Google Drive con acceso controlado.",

    // Profile
    "profile.title": "Mi Perfil",
    "profile.personal": "Información personal",
    "profile.contact": "Datos de contacto",
    "profile.settings": "Configuración",
    "profile.save": "Guardar cambios",
    "profile.page.title": "Mi Perfil",
    "profile.page.subtitle":
      "Administra tu información personal y configuraciones de cuenta",
    "profile.actions.exportData": "Exportar Datos",
    "profile.actions.editProfile": "Editar Perfil",
    "profile.certifiedInvestor": "Inversor Certificado",
    "profile.memberSince": "Miembro desde",
    "profile.riskProfile": "Perfil de Riesgo",
    "profile.invested": "Invertido",
    "profile.actions.changePhoto": "Cambiar Foto",
    "profile.tabs.personal": "Personal",
    "profile.tabs.preferences": "Preferencias",
    "profile.tabs.notifications": "Notificaciones",
    "profile.tabs.documentation": "Documentación",
    "profile.tabs.security": "Seguridad",
    "profile.personal.title": "Información Personal",
    "profile.personal.subtitle":
      "Actualiza tu información personal y datos de contacto",
    "profile.personal.fullName": "Nombre Completo",
    "profile.personal.email": "Correo Electrónico",
    "profile.personal.phone": "Teléfono",
    "profile.personal.address": "Dirección",
    "profile.personal.investmentGoals": "Objetivos de Inversión",
    "profile.personal.saveChanges": "Guardar Cambios",
    "profile.saving": "Procesando…",
    "profile.validation.nameRequired": "El nombre no puede estar vacío",

    // Security / Password
    "profile.security.title": "Seguridad",
    "profile.security.subtitle": "Gestiona la seguridad de tu cuenta",
    "profile.security.changePassword": "Cambiar Contraseña",
    "profile.security.changePassword.desc": "Actualiza tu contraseña regularmente para mantener tu cuenta segura",
    "profile.security.currentPassword": "Contraseña Actual",
    "profile.security.newPassword": "Nueva Contraseña",
    "profile.security.confirmPassword": "Confirmar Nueva Contraseña",
    "profile.security.minChars": "Mínimo 8 caracteres",
    "profile.security.oneNumber": "Un número",
    "profile.security.passwordsNotMatch": "Las contraseñas no coinciden",
    "profile.security.updating": "Actualizando...",
    "profile.security.updatePassword": "Cambiar Contraseña",
    "profile.security.success": "Contraseña actualizada correctamente. Todas las demás sesiones han sido cerradas.",
    "profile.security.error": "Error al cambiar la contraseña",

    // Additional profile translations
    "profile.success.dataExported.title": "Datos exportados",
    "profile.success.dataExported.message":
      "Tus datos han sido exportados correctamente.",
    "profile.success.photoUpdated.title": "Foto actualizada",
    "profile.success.photoUpdated.message":
      "Tu foto de perfil ha sido actualizada correctamente.",
    "profile.success.personalSaved.title": "Cambios guardados",
    "profile.success.personalSaved.message":
      "Tu información personal ha sido actualizada correctamente.",
    "profile.toast.editModeActivated": "Modo de edición activado",

    // Academy
    "academy.title": "The HNWI Academy",
    "academy.subtitle":
      "Centro de formación especializado para inversores de alto patrimonio",
    "academy.courses": "Cursos disponibles",
    "academy.progress": "Tu progreso",
    "academy.featured.badge": "Curso Destacado",
    "academy.featured.title": "Estrategias de Diversificación Global",
    "academy.featured.description":
      "Aprende las mejores prácticas para diversificar tu cartera a nivel internacional y reducir riesgos mientras maximizas oportunidades.",
    "academy.featured.start": "Comenzar Curso",
    "academy.progress.completed": "Cursos completados",
    "academy.progress.certifications": "Certificaciones",
    "academy.progress.studyHours": "Horas de estudio",
    "academy.nextWebinar.title": "Próximo Webinar",
    "academy.nextWebinar.join": "Unirse al Webinar",
    "academy.courses.available": "Cursos Disponibles",
    "academy.courses.viewAll": "Ver todos",
    "academy.courses.duration": "duración",
    "academy.courses.level.beginner": "Principiante",
    "academy.courses.level.intermediate": "Intermedio",
    "academy.courses.level.advanced": "Avanzado",
    "academy.courses.status.available": "Disponible",
    "academy.courses.status.inProgress": "En progreso",
    "academy.courses.status.comingSoon": "Próximamente",
    "academy.courses.completed": "completado",
    "academy.courses.continue": "Continuar",
    "academy.courses.start": "Comenzar",
    "academy.webinars.upcoming": "Próximos Webinars",
    "academy.webinars.viewCalendar": "Ver calendario",
    "academy.webinars.live": "LIVE",
    "academy.webinars.register": "Registrarse Gratis",
    "academy.course1.title": "Fundamentos de Inversión Inmobiliaria",
    "academy.course1.description":
      "Conceptos básicos para entender el mercado inmobiliario y sus oportunidades.",
    "academy.course2.title": "Análisis de Mercados Internacionales",
    "academy.course2.description":
      "Cómo evaluar oportunidades de inversión en diferentes países y regiones.",
    "academy.course3.title": "Gestión de Riesgos en Portfolio",
    "academy.course3.description":
      "Estrategias avanzadas para minimizar riesgos y maximizar retornos.",
    "academy.course4.title": "Fiscalidad Internacional",
    "academy.course4.description":
      "Aspectos fiscales de las inversiones internacionales y optimización tributaria.",
    "academy.webinar1.title": "Tendencias del Mercado Q1 2025",
    "academy.webinar1.speaker": "Dr. Ana Martínez",
    "academy.webinar2.title": "Oportunidades en Mercados Emergentes",
    "academy.webinar2.speaker": "Carlos Rodríguez",

    // Common
    "common.loading": "Cargando...",
    "common.error": "Error",
    "common.success": "Éxito",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",
    "common.edit": "Editar",
    "common.delete": "Eliminar",
    "common.save": "Guardar",
    "common.close": "Cerrar",
    "common.search": "Buscar",
    "common.filter": "Filtrar",
    "common.export": "Exportar",
    "common.import": "Importar",
    "common.more": "Más opciones",

    // Financial terms
    "financial.nav": "NAV",
    "financial.performance": "Rendimiento",
    "financial.return": "Retorno",
    "financial.value": "Valor",
    "financial.growth": "Crecimiento",
    "financial.profit": "Beneficio",
    "financial.loss": "Pérdida",
    "financial.total": "Total",
    "financial.invested": "Invertido",
    "financial.available": "Disponible",
    "financial.pending": "Pendiente",

    // Time periods
    "time.today": "Hoy",
    "time.week": "Semana",
    "time.month": "Mes",
    "time.quarter": "Trimestre",
    "time.year": "Año",
    "time.ytd": "YTD",
    "time.since_inception": "Desde inicio",

    // Charts - Light Theme Only
    "nav.title": "Evolución NAV",
    "nav.since": "Desde",
    "nav.ytd": "YTD",
    "nav.vsBm": "vs benchmark",
    "dist.title": "Distribución por Tipo",
    "dist.long": "Larga Temporada",
    "dist.rooms": "Habitaciones",
    "dist.short": "Temporal",

    // Months for Chart Axes
    "m.jan": "Ene",
    "m.feb": "Feb",
    "m.mar": "Mar",
    "m.apr": "Abr",
    "m.may": "May",
    "m.jun": "Jun",
    "m.jul": "Jul",
    "m.aug": "Ago",
    "m.sep": "Sep",
    "m.oct": "Oct",
    "m.nov": "Nov",
    "m.dec": "Dic",

    // Number formatting
    "num.thousands_sep": ".",
    "num.decimal_sep": ",",
  },
  en: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.miInversion": "My Investment",
    "nav.portfolio": "Portfolio",
    "nav.deals": "Pro-Rates",
    "nav.reportes": "Reports",
    "nav.reportes.financiero": "Financial Information",
    "nav.reportes.operativo": "Operational Reports",
    "nav.reportes.documentacion": "Documentation",
    "nav.profile": "Profile",
    "nav.academy": "The HNWI Academy",
    "nav.feedback": "Feedback",
    "nav.logout": "Sign Out",
      "nav.fees": "Fees",

    // Fees labels
    "fees.pageSize.label": "Page size",
    "fees.filter.status": "Status",
    "fees.filter.all": "All",
    "fees.filter.paid": "Paid",
    "fees.filter.pending": "Pending",
    "pagination.prev": "Previous",
    "pagination.next": "Next",
    "pagination.page": "Page",
    "pagination.of": "of",

    // User info
    "user.role": "Partner",
    "user.profile": "My Profile",

    // Login
    "login.title": "Partners Portal",
    "login.email": "Email",
    "login.password": "Password",
    "login.button": "Access as Partner",
    "login.tagline": "Access your account to manage your investments",
    "login.rememberMe": "Remember me",
    "login.forgotPassword": "Forgot your password?",
    "login.or": "OR",
    "login.support": "Contact Support: support@bellamysignature.com",

    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.welcome": "Welcome back,",
    "dashboard.overview": "Portfolio Overview",
    "dashboard.performance": "Performance",
    "dashboard.nav.evolution": "NAV Evolution",
    "dashboard.nav.current": "Current NAV",
    "dashboard.nav.ytd": "YTD",
    "dashboard.nav.live": "LIVE",
    "dashboard.nav.lastUpdated": "Last Updated",
    "dashboard.nav.totalValue": "Total Portfolio Value",
    "dashboard.nav.performanceTitle": "Global Performance",
    "dashboard.nav.distributionTitle": "Distribution by Type",
    "dashboard.nav.evolutionTitle": "NAV Evolution",
    "dashboard.summary.totalInvested": "Total Invested",
    "dashboard.summary.currentValue": "Current Value",
    "dashboard.summary.totalReturn": "Total Return",
    "dashboard.summary.unrealizedGains": "Unrealized Gains",
    "dashboard.kpi.activoTotal": "Total Assets",
    "dashboard.kpi.rentasYTD": "Rents YTD",
    "dashboard.kpi.cashOnCash": "Cash on Cash",
    "dashboard.kpi.feesYTD": "Fees YTD",
    "dashboard.kpi.feesTotal": "Total Fees",
    "dashboard.activity.title": "Recent Activity",
    "dashboard.activity.subtitle": "Latest movements in your portfolio",
    "dashboard.activity.rent": "Rent",
    "dashboard.table.date": "Date",
    "dashboard.table.asset": "Asset",
    "dashboard.table.type": "Type",
    "dashboard.table.amount": "Amount",
    "dashboard.table.status": "Status",
    "dashboard.status.completed": "Completed",

    // Portfolio
    "portfolio.title": "Portfolio",
    "portfolio.overview": "Investment Overview",
    "portfolio.totalValue": "Total Value",
    "portfolio.performance": "Performance",
    "portfolio.allocation": "Allocation",
    "portfolio.tableHeaders.property": "Property",
    "portfolio.tableHeaders.location": "Location",
    "portfolio.tableHeaders.type": "Type",
    "portfolio.tableHeaders.area": "Area",
    "portfolio.tableHeaders.investment": "Investment",
    "portfolio.tableHeaders.currentValue": "Current Value",
    "portfolio.tableHeaders.yield": "Yield",
    "portfolio.tableHeaders.status": "Status",
    "portfolio.filters.all": "All",
    "portfolio.filters.residential": "Residential",
    "portfolio.filters.commercial": "Commercial",
    "portfolio.filters.industrial": "Industrial",
    "portfolio.status.active": "Active",
    "portfolio.status.sold": "Sold",
    "portfolio.status.construction": "Under Construction",
    "portfolio.header.title": "Portfolio",
    "portfolio.header.subtitle":
      "Complete view of your real estate investments",
    "portfolio.table.property": "Property",
    "portfolio.table.location": "Location",
    "portfolio.table.type": "Type",
    "portfolio.table.investment": "Investment",
    "portfolio.table.currentValue": "Current Value",
    "portfolio.table.return": "Return",
    "portfolio.table.status": "Status",
    "portfolio.table.annualRent": "Annual Rent",
    "portfolio.table.mortgage": "Mortgage",
    "portfolio.table.purchaseDate": "Purchase Date",
    "portfolio.table.firstRentDate": "1st Rental Date",
    "portfolio.table.vacancy12m": "Vacancy 12M",
    "portfolio.filter.all": "All",
    "portfolio.filter.commercial": "Commercial",
    "portfolio.filter.residential": "Residential",
    "portfolio.filter.industrial": "Industrial",
    "portfolio.status.development": "In Development",
    "portfolio.status.renting": "Renting",
    "portfolio.status.repositioning": "Repositioning",
    "portfolio.status.underContract": "Under Contract",
    "portfolio.actions.viewDetail": "View Detail",
    "portfolio.totals.total": "TOTAL",
    "portfolio.totals.assets": "Assets",
    "portfolio.empty.noResults": "Oh, it looks like there’s nothing here",

    // Investment (Aportaciones) - Complete English translations
    "investment.title": "My Investment",
    "investment.summary": "Investment Summary",
    "investment.total": "Total Invested",
    "investment.date": "Date",
    "investment.amount": "Amount",
    "investment.status": "Status",
    "investment.contributions": "Contributions",
    "investment.totalInvested": "Total Invested",
    "investment.contributionsMade": "Contributions Made",
    "investment.averageContribution": "Average Contribution",
    "investment.totalContributions": "Total Contributions",
    "investment.reference": "Reference",
    "investment.method": "Method",
    "investment.pending": "Pending",
    "investment.completed": "Completed",
    "investment.processing": "Processing",
    "investment.summary.totalContributed": "Total Contributed",
    "investment.summary.totalGains": "Total Gains",
    "investment.header.title": "Investment Evolution",
    "investment.header.subtitle":
      "Comparison between contributed capital and current value of your investment",
    "investment.evolution.title": "Your Investment Evolution",
    "investment.evolution.subtitle":
      "Comparison between contributed capital and current value of your investment",
    "investment.metrics.completedContributions": "completed contributions",
    "investment.metrics.performanceOverContributed": "over contributed",
    "investment.history.title": "My Investment",
    "investment.history.subtitle":
      "Complete history of your contributions and returns",
    "investment.table.concept": "Concept",
    "investment.table.amount": "Amount",
    "investment.table.date": "Date",
    "investment.table.viewDocument": "View document",
    "investment.table.noDocument": "No document available",
    "investment.table.status": "Status",
    "investment.concepts.initial": "Initial contribution",
    "investment.concepts.additional": "Additional contribution",
    "investment.concepts.q3": "Q3 contribution",
    "investment.status.completed": "Completed",
    "investment.status.pending": "Pending",
    "investment.status.pendingRequest": "Pending request",
    "investment.actions.contributeMore": "Contribute More",
    "investment.actions.requestAuthorization": "Request Authorization",
    "investment.actions.cancel": "Cancel",
    "investment.actions.close": "Close",
    "investment.tooltip.contributionsBlocked":
      "Contributions temporarily blocked. Request authorization to make a new contribution.",
    "investment.modal.request.title": "Request new contribution",
    "investment.modal.request.description":
      "The contribution system is currently blocked. You can request authorization to make a new contribution and our team will contact you.",
    "investment.modal.request.amountLabel": "Desired amount",
    "investment.modal.request.amountPlaceholder": "e.g. €50,000",
    "investment.modal.request.conceptLabel": "Concept",
    "investment.modal.request.conceptPlaceholder": "Q4 additional contribution",
    "investment.modal.success.title": "Request sent successfully",
    "investment.modal.success.description":
      "Your request has been sent successfully. Our team will review the request and contact you within 24-48 hours.",
    "investment.chart.totalContributed": "Total Contributed",
    "investment.chart.currentValue": "Current Value",

    // Deals
    "deals.title": "Pro-Rates",
    "deals.subtitle":
      "Explore the latest available real estate investment opportunities.",
    "deals.header.title": "Available Opportunities",
    "deals.active": "Active Opportunities",
    "deals.new": "New Opportunity",
    "deals.status": "Status",
    "deals.type": "Type",
    "deals.location": "Location",
    "deals.investment": "Minimum Investment",
    "deals.metrics.units": "UNITS",
    "deals.metrics.builtArea": "Built Area",
    "deals.metrics.roomPotential": "Room Potential",
    "deals.commercial.locals": "Commercial premises",
    "deals.sections.general": "General",
    "deals.sections.strategy": "Strategy",
    "deals.sections.timing": "Timing",
    "deals.fields.name": "Name",
    "deals.fields.address": "Address",
    "deals.fields.city": "City",
    "deals.fields.catastralRef": "Cadastral Reference",
    "deals.fields.strategy": "Strategy",
    "deals.fields.typology": "Typology",
    "deals.strategy.description":
      "Buy, divide, liquidate commercial premises and operate rooms",
    "deals.typology.residential": "Residential and Commercial",
    "deals.timeline.negotiations": "Start negotiations",
    "deals.timeline.contract": "Sign earnest money contract",
    "deals.timeline.purchase": "Set purchase date",
    "deals.actions.viewDetails": "View Details",
    "deals.actions.investNow": "Invest Now",
    "deals.actions.viewMaps": "Click to open Google Maps",

    // Reports
    "reports.title": "Reports",
    "reports.financial": "Financial Information",
    "reports.operational": "Operational Reports",
    "reports.documentation": "Documentation",
    "reports.download": "Download",
    "reports.view": "View Document",
    "reports.monthly": "Monthly Report",
    "reports.quarterly": "Quarterly Report",
    "reports.annual": "Annual Report",
    "reports.status.available": "Available",
    "reports.status.processing": "Processing",
    "reports.date": "Date",
    "reports.type": "Type",
    "reports.actions": "Actions",
    "reports.financial.title": "Financial Reports",
    "reports.financial.subtitle":
      "Financial statements and performance reports",
    "reports.operational.title": "Operational Reports",
    "reports.operational.subtitle":
      "Operational and property management reports",
    "reports.documentation.title": "Legal Documentation",
    "reports.documentation.subtitle": "Contracts, deeds and legal documents",
    "reports.tabs.financial": "Financial Information",
    "reports.tabs.operational": "Operational Reports",
    "reports.tabs.documentation": "Documentation",

    // Document categories
    "docs.governance.title": "Governance - Shareholders Agreement",
    "docs.governance.subtitle": "Corporate governance agreements and policies",
    "docs.capitalExtensions.title": "Capital Extensions 2024",
    "docs.capitalExtensions.subtitle":
      "Capital increase operations documentation",
    "docs.taxes.title": "Taxes",
    "docs.taxes.subtitle": "Tax returns and tax documentation",
    "docs.other.title": "Other Documentation",
    "docs.other.subtitle": "Additional documents and corporate archive",
    "docs.secure.title": "Secure Documentation",
    "docs.secure.subtitle":
      "All documents are securely stored in Google Drive with controlled access.",

    // Profile
    "profile.title": "My Profile",
    "profile.personal": "Personal Information",
    "profile.contact": "Contact Details",
    "profile.settings": "Settings",
    "profile.save": "Save Changes",
    "profile.page.title": "My Profile",
    "profile.page.subtitle":
      "Manage your personal information and account settings",
    "profile.actions.exportData": "Export Data",
    "profile.actions.editProfile": "Edit Profile",
    "profile.certifiedInvestor": "Certified Investor",
    "profile.memberSince": "Member since",
    "profile.riskProfile": "Risk Profile",
    "profile.invested": "Invested",
    "profile.actions.changePhoto": "Change Photo",
    "profile.tabs.personal": "Personal",
    "profile.tabs.preferences": "Preferences",
    "profile.tabs.notifications": "Notifications",
    "profile.tabs.documentation": "Documentation",
    "profile.tabs.security": "Security",
    "profile.personal.title": "Personal Information",
    "profile.personal.subtitle":
      "Update your personal information and contact details",
    "profile.personal.fullName": "Full Name",
    "profile.personal.email": "Email Address",
    "profile.personal.phone": "Phone Number",
    "profile.personal.address": "Address",
    "profile.personal.investmentGoals": "Investment Goals",
    "profile.personal.saveChanges": "Save Changes",

    // Security / Password
    "profile.security.title": "Security",
    "profile.security.subtitle": "Manage your account security",
    "profile.security.changePassword": "Change Password",
    "profile.security.changePassword.desc": "Update your password regularly to keep your account secure",
    "profile.security.currentPassword": "Current Password",
    "profile.security.newPassword": "New Password",
    "profile.security.confirmPassword": "Confirm New Password",
    "profile.security.minChars": "Minimum 8 characters",
    "profile.security.oneNumber": "A number",
    "profile.security.passwordsNotMatch": "Passwords do not match",
    "profile.security.updating": "Updating...",
    "profile.security.updatePassword": "Change Password",
    "profile.security.success": "Password updated successfully. All other sessions have been closed.",
    "profile.security.error": "Error changing password",

    // Additional profile translations
    "profile.success.dataExported.title": "Data exported",
    "profile.success.dataExported.message":
      "Your data has been exported successfully.",
    "profile.success.photoUpdated.title": "Photo updated",
    "profile.success.photoUpdated.message":
      "Your profile photo has been updated successfully.",
    "profile.success.personalSaved.title": "Changes saved",
    "profile.success.personalSaved.message":
      "Your personal information has been updated successfully.",
    "profile.toast.editModeActivated": "Edit mode activated",

    // Academy
    "academy.title": "The HNWI Academy",
    "academy.subtitle":
      "Specialized training center for high net worth investors",
    "academy.courses": "Available courses",
    "academy.progress": "Your progress",
    "academy.featured.badge": "Featured Course",
    "academy.featured.title": "Global Diversification Strategies",
    "academy.featured.description":
      "Learn best practices to diversify your portfolio internationally and reduce risks while maximizing opportunities.",
    "academy.featured.start": "Start Course",
    "academy.progress.completed": "Courses completed",
    "academy.progress.certifications": "Certifications",
    "academy.progress.studyHours": "Study hours",
    "academy.nextWebinar.title": "Next Webinar",
    "academy.nextWebinar.join": "Join Webinar",
    "academy.courses.available": "Available Courses",
    "academy.courses.viewAll": "View all",
    "academy.courses.duration": "duration",
    "academy.courses.level.beginner": "Beginner",
    "academy.courses.level.intermediate": "Intermediate",
    "academy.courses.level.advanced": "Advanced",
    "academy.courses.status.available": "Available",
    "academy.courses.status.inProgress": "In Progress",
    "academy.courses.status.comingSoon": "Coming Soon",
    "academy.courses.completed": "completed",
    "academy.courses.continue": "Continue",
    "academy.courses.start": "Start",
    "academy.webinars.upcoming": "Upcoming Webinars",
    "academy.webinars.viewCalendar": "View calendar",
    "academy.webinars.live": "LIVE",
    "academy.webinars.register": "Register Free",
    "academy.course1.title": "Real Estate Investment Fundamentals",
    "academy.course1.description":
      "Basic concepts to understand the real estate market and its opportunities.",
    "academy.course2.title": "International Market Analysis",
    "academy.course2.description":
      "How to evaluate investment opportunities in different countries and regions.",
    "academy.course3.title": "Portfolio Risk Management",
    "academy.course3.description":
      "Advanced strategies to minimize risks and maximize returns.",
    "academy.course4.title": "International Taxation",
    "academy.course4.description":
      "Tax aspects of international investments and tax optimization.",
    "academy.webinar1.title": "Q1 2025 Market Trends",
    "academy.webinar1.speaker": "Dr. Ana Martínez",
    "academy.webinar2.title": "Emerging Market Opportunities",
    "academy.webinar2.speaker": "Carlos Rodríguez",

    // Common
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.save": "Save",
    "common.close": "Close",
    "common.search": "Search",
    "common.filter": "Filter",
    "common.export": "Export",
    "common.import": "Import",
    "common.more": "More options",

    // Financial terms
    "financial.nav": "NAV",
    "financial.performance": "Performance",
    "financial.return": "Return",
    "financial.value": "Value",
    "financial.growth": "Growth",
    "financial.profit": "Profit",
    "financial.loss": "Loss",
    "financial.total": "Total",
    "financial.invested": "Invested",
    "financial.available": "Available",
    "financial.pending": "Pending",

    // Time periods
    "time.today": "Today",
    "time.week": "Week",
    "time.month": "Month",
    "time.quarter": "Quarter",
    "time.year": "Year",
    "time.ytd": "YTD",
    "time.since_inception": "Since inception",

    // Charts - Light Theme Only
    "nav.title": "NAV Evolution",
    "nav.since": "Since",
    "nav.ytd": "YTD",
    "nav.vsBm": "vs benchmark",
    "dist.title": "Distribution by Type",
    "dist.long": "Long Term",
    "dist.rooms": "Rooms",
    "dist.short": "Short Term",

    // Months for Chart Axes
    "m.jan": "Jan",
    "m.feb": "Feb",
    "m.mar": "Mar",
    "m.apr": "Apr",
    "m.may": "May",
    "m.jun": "Jun",
    "m.jul": "Jul",
    "m.aug": "Aug",
    "m.sep": "Sep",
    "m.oct": "Oct",
    "m.nov": "Nov",
    "m.dec": "Dec",

    // Number formatting
    "num.thousands_sep": ",",
    "num.decimal_sep": ".",
  },
};

// Language provider component
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("es");

  // Load language from localStorage on mount
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem("language") as Language;
      if (savedLanguage && (savedLanguage === "es" || savedLanguage === "en")) {
        setLanguage(savedLanguage);
      }
    } catch (error) {
      console.warn("Failed to load language from localStorage:", error);
    }
  }, []);

  // Save language to localStorage when it changes
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    try {
      localStorage.setItem("language", lang);
    } catch (error) {
      console.warn("Failed to save language to localStorage:", error);
    }
  };

  // Translation function
  const t = (key: string): string => {
    return (
      translations[language][
        key as keyof (typeof translations)[typeof language]
      ] || key
    );
  };

  // Bilingual text function for elements that might need both languages
  const getBilingualText = (key: string): string | JSX.Element => {
    const text = t(key);
    return text;
  };

  const value: LanguageContextType = {
    language,
    setLanguage: changeLanguage,
    t,
    getBilingualText,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
